import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { Search, RefreshCw, AlertCircle, FileX, ArrowLeft, Trash2, Wallet, X, } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../Apis/apiConfig";
import "../Style/Member.css";
import "../Style/FinancialTables.css";
import "../Style/Dashboard.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx-js-style";

const AdvancePaymentDeleted = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = advancePayments.length;
  const totalPages = Math.ceil(totalItems / perPage);

  const filteredAdvancePayments = useMemo(() => {
    return advancePayments.filter(
      (payment) =>
        payment.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.unitNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.amount?.toString().includes(searchTerm),
    );
  }, [advancePayments, searchTerm]);

  const paginated = filteredAdvancePayments.slice(
    (page - 1) * perPage,
    page * perPage,
  );
  const displayedTotal = filteredAdvancePayments.length;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
      setCurrentPage(p);
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: "This will delete ALL deleted advance payment records permanently! This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const societyName = localStorage.getItem("society");

          await axios.delete(
            `${BASE_URL}/advance-payment/delete-all-deleted-advancements`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: { societyName },
            },
          );

          setAdvancePayments([]);
          return true;
        } catch (err) {
          console.error("Error deleting all advance payments:", err);
          Swal.showValidationMessage("Failed to delete all advance payments");
          return false;
        }
      },
    });

    if (result.isConfirmed && result.value) {
      Swal.fire(
        "Deleted!",
        "All deleted advance payments have been removed.",
        "success",
      );
    }
  };


  const handleDownloadExcel = () => {
  if (!advancePayments || advancePayments.length === 0) {
    Swal.fire("No Data", "No records available to export.", "warning");
    return;
  }

  // Dynamic headers from API response
  const excelData = advancePayments.map((item) => {
    const row = {};

    Object.keys(item).forEach((key) => {
      let value = item[key];

      // Format date columns automatically
      if (
        key.toLowerCase().includes("date") &&
        value
      ) {
        value = formatDate(value);
      }

      row[key] = value;
    });

    return row;
  });

  const ws = XLSX.utils.json_to_sheet(excelData);

  // Auto width
  const headers = Object.keys(excelData[0]);
  ws["!cols"] = headers.map((header) => ({
    wch: Math.max(header.length + 5, 20),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Deleted Advance Payments");

  XLSX.writeFile(
    wb,
    `Deleted_Advance_Payments_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`
  );
};

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const societyName = localStorage.getItem("society");
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${BASE_URL}/advance-payment/get-deleted-advancement`,
        null,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      
      setAdvancePayments(response.data || []);
      setPage(1);
      setCurrentPage(1);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPage(1);
    setCurrentPage(1);
  }, [searchTerm]);

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${totalPages === currentPage ? "active" : ""
              }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }
    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return "-";
    const num = parseFloat(amount);
    return isNaN(num) ? amount : `₹${num.toLocaleString("en-IN")}`;
  };

  return (
    <div className="app-container">
      <style>
        {`
        /* ========== RECEIPT PAGE OPTIMIZATIONS ========== */
        .app-container {
          height: 100vh;
          overflow: hidden !important;
        }

        .main-layout {
          height: calc(100vh - 64px);
          overflow: hidden !important;
        }

        .content-area {
          height: 100%;
          overflow-y: auto !important;
          overflow-x: hidden;
          padding-bottom: 20px;
        }

        .financial-container,
        .financial-card {
          height: auto !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .financial-body {
          height: auto !important;
          overflow: visible !important;
          padding: 0 !important;
        }

        /* ========== COMPACT TABLE CONTROLS ========== */
        .controls-left,
        .controls-center,
        .controls-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .controls-center {
          flex: 1;
          justify-content: center;
        }

        .entries-selector label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .entries-select {
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
          font-size: 12px;
          color: #334155;
          cursor: pointer;
          height: 28px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
          border: none;
        }

        .pagination-nav {
          padding: 4px 10px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          height: 26px;
          transition: all 0.2s;
        }

        .pagination-nav:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-nav:not(:disabled):hover {
          background: #f1f5f9;
        }

        .pagination-btn {
          min-width: 26px;
          height: 26px;
          padding: 4px 8px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover {
          background: #f1f5f9;
        }

        .pagination-btn.active {
          background: #0d6efd;
          color: white;
          border-color: #0d6efd;
        }

        .pagination-ellipsis {
          padding: 0 4px;
          color: #94a3b8;
          font-size: 11px;
        }

        .entries-info {
          font-size: 11px;
          color: #64748b;
          white-space: nowrap;
        }

        /* ========== TABLE OPTIMIZATION ========== */
        .financial-table-container {
          overflow-x: auto;
          padding: 0;
          margin: 0;
        }

        .financial-table,
        .data-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .financial-table th,
        .data-table th {
          padding: 6px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          white-space: nowrap;
          font-weight: 600;
          font-size: 11px !important;
          background-color: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.2 !important;
          height: 32px !important;
        }

        .financial-table td,
        .data-table td {
          padding: 5px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          font-size: 10px !important;
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.3 !important;
          height: 36px !important;
        }

        /* SR NO Column */
        .financial-table th:nth-child(1),
        .financial-table td:nth-child(1),
        .data-table th:first-child,
        .data-table td:first-child {
          width: 35px !important;
          min-width: 35px !important;
          max-width: 35px !important;
          padding: 5px 2px !important;
        }

        /* Cell Content */
        .cell-content {
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        /* Row Hover */
        .financial-table tbody tr:hover td,
        .data-table tbody tr:hover td {
          background-color: #f8fafc;
        }

        .selected-row td {
          background-color: #e3f2fd !important;
        }

        /* Empty State */
        .financial-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          min-height: 300px;
        }

        .financial-state h3 {
          margin: 16px 0 8px 0;
          color: #334155;
          font-size: 18px;
          font-weight: 600;
        }

        .financial-state p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        /* Loading Animation */
        .spin-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .controls-left,
          .controls-center,
          .controls-right {
            width: 100%;
            justify-content: space-between;
          }
        }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              <div className="flex px-4 bg-blue-400 rounded-tr-lg rounded-tl-lg text-white py-2">
                <div className="controls-left">
                  <button
                    onClick={() => navigate(-1)}
                    className="back-button"
                    title="Go back"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      "rgba(255,255,255,0.2)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <ArrowLeft size={16} />
                  </button>

                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                  <button
  className="rounded border-none flex bg-green-500 text-white hover:bg-green-600 p-2"
  onClick={handleDownloadExcel}
  title="Download Excel"
>
  <Wallet size={14} />
</button>
                </div>
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search deleted advance payments..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setPage(1);
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={handleRefresh}
                      disabled={loading}
                      title="Refresh deleted advance payments list"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={handleDeleteAll}
                      title="Delete all advance payment history records"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="controls-right">
                  {!loading && displayedTotal > 0 && (
                    <span className="entries-info text-white">
                      {(page - 1) * perPage + 1}-
                      {Math.min(page * perPage, displayedTotal)} of{" "}
                      {displayedTotal}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      Prev
                    </button>
                    {renderPageNumbers()}
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Deleted Advance Payments</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchData}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : advancePayments.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Deleted Advance Payments Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No deleted advance payments match your search criteria."
                          : "There are no deleted advance payments recorded in the history."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SR NO.</th>
                          <th>MEMBER NAME</th>
                          <th>UNIT NO</th>
                          <th>AMOUNT</th>
                          <th>DELETED DATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((row, idx) => {
                          const absoluteIdx = (page - 1) * perPage + idx;
                          return (
                            <tr key={row.advancePaymentDeletedId || idx}>
                              <td>{absoluteIdx + 1}</td>
                              <td
                                style={{
                                  textAlign: "left",
                                  paddingLeft: "12px",
                                }}
                              >
                                <span className="cell-content">
                                  {row.memberName || "-"}
                                </span>
                              </td>
                              <td>{row.unitNo || "-"}</td>
                              <td
                                style={{ color: "#28a745", fontWeight: "600" }}
                              >
                                {formatAmount(row.amount)}
                              </td>
                              <td style={{ color: "#dc3545" }}>
                                {formatDate(row.deletedDate)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvancePaymentDeleted;
